import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import Navbar from "../../Header/header";
import SASidebar from "../../Dashboard/SuperAdmin/Superadmincom/SuperAdminSidebar";
import { API_LoginURL } from "../../../config";
import Swal from "sweetalert2";
import ReactPaginate from "react-paginate";
import { ClipLoader } from "react-spinners";
import Select from 'react-select';
import { debounce } from 'lodash';

export default function QuizMaster() {
  // State management
  const [state, setState] = useState({
    classes: [],
    quizzes: [],
    loading: true,
    formSubmitting: false,
    formData: {
      quizId: 0,
      classId: 0,
      quizTittle: "",
      quizDescription: "",
      quizStatus: true
    },
    searchQuery: "",
    filterStatus: "all",
    currentPage: 0,
    itemsPerPage: 5
  });

  // Helper function to update state
  const updateState = (newState) => {
    setState(prev => ({ ...prev, ...newState }));
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchQuizzes();
    fetchClasses();
  }, []);

  // API calls
  const fetchClasses = async () => {
    try {
      const response = await axios.get(`${API_LoginURL}class/ClassMasterList`, {
        headers: getHeaders(),
      });
      updateState({ classes: Array.isArray(response.data) ? response.data : [] });
    } catch (error) {
      console.error("Error fetching classes:", error);
      Swal.fire("Error!", "Failed to load classes.", "error");
    }
  };

  const fetchQuizzes = async () => {
    updateState({ loading: true });
    try {
      const response = await axios.get(`${API_LoginURL}quiz/QuizMasterList`, {
        headers: getHeaders(),
      });
      updateState({
        quizzes: Array.isArray(response.data) ? response.data : [],
        loading: false
      });
    } catch (error) {
      console.error("Error fetching quizzes:", error);
      Swal.fire("Error!", "Failed to load quizzes.", "error");
      updateState({ loading: false });
    }
  };

  // Helper function for headers
  const getHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: localStorage.getItem("token")
  });

  // Form handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    updateState({
      formData: {
        ...state.formData,
        [name]: value
      }
    });
  };

  const handleSelectChange = (selectedOption) => {
    updateState({
      formData: {
        ...state.formData,
        classId: selectedOption ? selectedOption.value : 0
      }
    });
  };

  // Debounced search
  const debouncedSearch = useMemo(
    () => debounce((query) => {
      updateState({ searchQuery: query, currentPage: 0 });
    }, 300),
    []
  );

  const handleSearchChange = (e) => {
    debouncedSearch(e.target.value);
  };

  const handleFilterChange = (e) => {
    updateState({
      filterStatus: e.target.value,
      currentPage: 0
    });
  };

  const resetForm = () => {
    updateState({
      formData: {
        quizId: 0,
        classId: 0,
        quizTittle: "",
        quizDescription: "",
        quizStatus: true
      }
    });
  };

  const handleEdit = (quiz) => {
    updateState({ 
      formData: {
        ...quiz,
        quizStatus: quiz.quizStatus !== false
      }
    });
  };

  const handleDelete = async (quizId) => {
    const confirmation = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!"
    });

    if (!confirmation.isConfirmed) return;
    let quizDetailsId = quizId

    try {
      await axios.post(
        `${API_LoginURL}quiz/DeleteQuizDetails`,
        { quizDetailsId },
        { headers: getHeaders() }
      );
      updateState({
        quizzes: state.quizzes.filter(item => item.quizId !== quizId)
      });
      Swal.fire("Deleted!", "Quiz has been deleted.", "success");
    } catch (error) {
      console.error("Error deleting quiz:", error);
      Swal.fire("Error", "Failed to delete quiz", "error");
    }
  };

  const handlePageChange = ({ selected }) => {
    updateState({ currentPage: selected });
  };

  const handleFormSubmit = async () => {
    updateState({ formSubmitting: true });
    try {
      const response = await axios.post(
        `${API_LoginURL}quiz/QuizMasterAddEdit`,
        state.formData,
        { headers: getHeaders() }
      );

      Swal.fire({
        icon: response.data.response === "SUCCESS" ? "success" : "error",
        title: response.data.response === "SUCCESS" ? "Success" : "Error",
        text: response.data.response === "SUCCESS" 
          ? "Quiz saved successfully" 
          : response.data.response,
      });

      if (response.data.response === "SUCCESS") {
        fetchQuizzes();
        document.getElementById("closeQuizModal").click();
        resetForm();
      }
    } catch (error) {
      console.error("Error saving quiz:", error);
      Swal.fire("Error", "Failed to save quiz", "error");
    } finally {
      updateState({ formSubmitting: false });
    }
  };

  // Filter and pagination
  const filteredQuizzes = state.quizzes.filter(item => {
    const matchesSearch = 
      item.quizTittle.toLowerCase().includes(state.searchQuery.toLowerCase()) ||
      item.quizDescription.toLowerCase().includes(state.searchQuery.toLowerCase());
    
    const matchesStatus = 
      state.filterStatus === "all" ||
      (state.filterStatus === "active" && item.quizStatus) ||
      (state.filterStatus === "inactive" && !item.quizStatus);
    
    return matchesSearch && matchesStatus;
  });

  const pageCount = Math.ceil(filteredQuizzes.length / state.itemsPerPage);
  const displayedQuizzes = filteredQuizzes.slice(
    state.currentPage * state.itemsPerPage,
    (state.currentPage + 1) * state.itemsPerPage
  );

  // Get class name for display
  const getClassName = (classId) => {
    const classItem = state.classes.find(c => c.classId === classId);
    return classItem ? classItem.className : "N/A";
  };

  return (
    <div className="container-scroller">
      <Navbar />
      <div className="container-fluid page-body-wrapper">
        <SASidebar />
        <div className="main-panel">
          <div className="content-wrapper">
            <div className="col-lg-12 grid-margin stretch-card">
              <div className="card">
                <div className="card-body">
                  {/* Header Section */}
                  <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-4">
                    <h4 className="card-title mb-3 mb-md-0">Quiz Management</h4>
                    
                    {/* Search and Filter */}
                    <div className="d-flex flex-column flex-md-row align-items-center gap-3 w-100 w-md-auto">
                      {/* Search */}
                      <div
                        className="flex-grow-1 me-md-2 position-relative"
                        style={{ maxWidth: "500px" }}
                      >
                        <input
                          type="text"
                          className="form-control form-control-sm ps-5 rounded-pill border-0"
                          placeholder="Search by Title or Description"
                          onChange={handleSearchChange}
                          style={{
                            height: "38px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                            transition: "all 0.3s ease",
                            backgroundColor: "#f8f9fa",
                          }}
                          onFocus={(e) => {
                            e.target.style.boxShadow = "0 2px 12px rgba(0,0,0,0.15)";
                            e.target.style.backgroundColor = "#fff";
                          }}
                          onBlur={(e) => {
                            e.target.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)";
                            e.target.style.backgroundColor = "#f8f9fa";
                          }}
                        />
                        <i className="fas fa-search position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>
                      </div>
                      
                      {/* Filter */}
                      {/* <div className="d-flex align-items-center">
                        <i className="fas fa-filter me-2 text-muted"></i>
                        <select
                          className="form-select"
                          value={state.filterStatus}
                          onChange={handleFilterChange}
                          style={{ minWidth: '120px' }}
                        >
                          <option value="all">All Status</option>
                          <option value="inactive">Active</option>
                          <option value="active">Inactive</option>
                        </select>
                      </div> */}
                      
                      {/* Add Quiz Button */}
                      <button
                        className="btn btn-primary d-flex align-items-center"
                        data-bs-toggle="modal"
                        data-bs-target="#quizModal"
                        onClick={resetForm}
                      >
                        <i className="fas fa-plus me-2"></i>
                        <span className="d-none d-md-inline">Add Quiz</span>
                      </button>
                    </div>
                  </div>

                  {/* Quiz Table */}
                  <div className="table-responsive">
                    {state.loading ? (
                      <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
                        <ClipLoader color="#4e73df" size={50} />
                        <p className="mt-3 ms-3">Loading quizzes...</p>
                      </div>
                    ) : (
                      <>
                        <table className="table table-hover table-striped">
                          <thead className="table-primary">
                            <tr>
                              <th width="10%">ID</th>
                              <th width="20%">Class</th>
                              <th width="25%">Title</th>
                              <th width="30%">Description</th>
                              {/* <th width="10%">Status</th> */}
                              <th width="10%" className="text-end">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {displayedQuizzes.length > 0 ? (
                              displayedQuizzes.map((item) => (
                                <tr key={item.quizId}>
                                  <td>{item.quizId}</td>
                                  <td>{getClassName(item.classId)}</td>
                                  <td>{item.quizTittle}</td>
                                  <td>{item.quizDescription}</td>
                                  {/* <td
                                    style={{
                                      color: item.quizStatus
                                        ? "red"
                                        : "green",
                                    }}
                                  >
                                    {item.quizStatus ? "InActive" : "Active"}
                                  
                                  </td> */}
                                  <td className="text-end">
                                    <div className="d-flex justify-content-end gap-2">
                                      <button
                                        className="btn btn-sm btn-outline-primary"
                                        data-bs-toggle="modal"
                                        data-bs-target="#quizModal"
                                        onClick={() => handleEdit(item)}
                                        title="Edit"
                                      >
                                        <i className="fas fa-edit"></i>
                                      </button>
                                      <button
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() => handleDelete(item.quizId)}
                                        title="Delete"
                                      >
                                        <i className="fas fa-trash"></i>
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="6" className="text-center py-4">
                                  <div className="d-flex flex-column align-items-center">
                                    <img
                                      src="https://img.icons8.com/ios/50/000000/no-camera--v1.png"
                                      alt="No quizzes"
                                      style={{ opacity: 0.5, width: '60px' }}
                                    />
                                    <p className="mt-3 text-muted">No quizzes found</p>
                                    <button
                                      className="btn btn-sm btn-primary mt-2"
                                      data-bs-toggle="modal"
                                      data-bs-target="#quizModal"
                                      onClick={resetForm}
                                    >
                                      Add New Quiz
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>

                        {/* Pagination */}
                        {displayedQuizzes.length > 0 && (
                          <ReactPaginate
                            previousLabel={<i className="fas fa-chevron-left"></i>}
                            nextLabel={<i className="fas fa-chevron-right"></i>}
                            pageCount={pageCount}
                            onPageChange={handlePageChange}
                            containerClassName={"pagination justify-content-center mt-4"}
                            pageClassName={"page-item"}
                            pageLinkClassName={"page-link"}
                            previousClassName={"page-item"}
                            previousLinkClassName={"page-link"}
                            nextClassName={"page-item"}
                            nextLinkClassName={"page-link"}
                            activeClassName={"active"}
                            breakLabel={"..."}
                            marginPagesDisplayed={2}
                            pageRangeDisplayed={5}
                            forcePage={state.currentPage}
                          />
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quiz Modal */}
      <div className="modal fade" id="quizModal" tabIndex="-1" aria-labelledby="quizModalLabel" aria-hidden="true">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header bg-light">
              <h5 className="modal-title" id="quizModalLabel">
                {state.formData.quizId > 0 ? 'Edit Quiz' : 'Add New Quiz'}
              </h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div className="modal-body">
              <form>
                <div className="row">
                <div className="col-md-6 mb-3 pe-0">
                  <label htmlFor="classId" className="form-label required">Class</label>
                  <Select
                    id="classId"
                    options={state.classes.map(classItem => ({
                      value: classItem.classId,
                      label: classItem.className
                    }))}
                    value={state.classes
                      .map(classItem => ({
                        value: classItem.classId,
                        label: classItem.className
                      }))
                      .find(option => option.value === state.formData.classId) || null}
                    onChange={handleSelectChange}
                    placeholder="Select Class"
                    className="basic-single"
                    classNamePrefix="select"
                    isClearable
                    isSearchable
                  />
                </div>
                <div className="col-md-5 flex-grow-1 mb-3 ps-0">
                  <label htmlFor="quizTittle" className="form-label required">Title</label>
                  <input
                    type="text"
                    className="form-control p-2"
                    id="quizTittle"
                    name="quizTittle"
                    value={state.formData.quizTittle}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                </div>
                <div className="row">
                <div className="mb-3">
                  <label htmlFor="quizDescription" className="form-label">Description</label>
                  <textarea
                    className="form-control"
                    id="quizDescription"
                    name="quizDescription"
                    rows="2"
                    value={state.formData.quizDescription}
                    onChange={handleInputChange}
                  />
                </div>
                </div>
                {/* {state.formData.quizId > 0 && (
                  <div className="mb-3">
                    <label htmlFor="quizStatus" className="form-label">Status</label>
                    <select
                      className="form-select"
                      id="quizStatus"
                      name="quizStatus"
                      value={state.formData.quizStatus}
                      onChange={handleInputChange}
                    >
                      <option value={true}>Active</option>
                      <option value={false}>Inactive</option>
                    </select>
                  </div>
                )} */}
              </form>
            </div>
            <div className="modal-footer">
              <button type="button" id="closeQuizModal" className="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleFormSubmit}
                disabled={state.formSubmitting}
              >
                {state.formSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Submitting...
                  </>
                ) : (
                  "Save Changes"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}